import {
    Component,
    ComponentRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    Type,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { ConfirmationWindowComponent } from '@components/confirmation-window/confirmation-window.component';
import { DashboardPanelAddFormComponent } from '@components/dashboard-panel-add-form/dashboard-panel-add-form.component';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { DashboardPanelInterface } from '@models/dashboard-panel-interface';
import { DashboardPanelFactoryService } from '@service/dashboard-panel-factory-service';
import { ResponsiveService } from '@service/responsive.service';
import { DashboardPanelParameters } from 'ops/common/models/dashboard-panel-parameters';

@Component({
    selector: 'app-dashboard-preview',
    templateUrl: './dashboard-preview.component.html',
    styleUrls: ['./dashboard-preview.component.scss'],
    standalone: false
})
export class DashboardPreviewComponent implements OnInit {
    selectedPanel: DashboardPanelParameters = {
        type: null,
        params: {},
    };
    @Output() onSelectionConfirmed = new EventEmitter<DashboardPanelParameters>();
    @Output() onClose = new EventEmitter<void>();

    @ViewChild('container', { read: ViewContainerRef, static: true })
    container: ViewContainerRef;

    @ViewChild(DashboardPanelAddFormComponent)
    dashboardPanelAddFormComponent: DashboardPanelAddFormComponent;

    @ViewChild('notification', { static: true })
    notification: PopUpNotificationComponent;

    @ViewChild('discardChangesOverlay')
    discardChangesOverlay: ConfirmationWindowComponent;

    isPreviewHide: boolean = true;

    component: ComponentRef<DashboardPanelInterface> = null;

    constructor(
        private panelFactory: DashboardPanelFactoryService,
        public responsive: ResponsiveService,
    ) {}

    ngOnInit(): void {
        this.responsive.breakPoinEmitter.subscribe(() => {
            if (!this.isPreviewHide && (this.responsive.isXLarge() || this.responsive.isLarge())) {
                this.isPreviewHide = true;
            }
        });
    }

    handleSaveClicked() {
        this.selectedPanel.type === null ? this.emitClose() : this.emitSelectionConfirmed();
    }

    emitSelectionConfirmed() {
        this.onSelectionConfirmed.emit(this.selectedPanel);
    }

    emitClose() {
        if (this.selectedPanel.type) {
            this.showDiscardOverlay();
        } else {
            this.onClose.emit();
        }
    }

    handleAddPanelSelection(panel: DashboardPanelParameters) {
        this.selectedPanel = panel;
        this.showPreview();
    }

    showPreview() {
        this.destroyComponent();
        let panelType: Type<DashboardPanelInterface>;

        try {
            panelType = this.panelFactory.getPanel(this.selectedPanel);
        } catch (err) {
            return;
        }

        let component = this.container.createComponent<DashboardPanelInterface>(panelType);

        this.component = component;
        this.container.element.nativeElement.appendChild(component.location.nativeElement);

        for (const i in this.selectedPanel.params) {
            component.instance[i] = this.selectedPanel.params[i];
        }

        if (component.instance.isPreview !== undefined) {
            component.instance.isPreview = true;
        }
        this.bindEvents(component);
    }

    destroyComponent() {
        if (this.component) {
            this.component.destroy();
            this.component.location.nativeElement.remove();
            this.component = null;
        }
    }

    bindEvents(component: ComponentRef<any>) {
        component.instance.onDeleteClicked?.subscribe(() => this.removeSelectedPanel());
        component.instance.onGoToPageClicked?.subscribe(() => this.onGoToPage());
    }

    removeSelectedPanel() {
        this.selectedPanel.params = {};
        this.selectedPanel.type = null;
        this.destroyComponent();
        this.dashboardPanelAddFormComponent.deleteSelectedPanel();
        if (this.shouldHidePreview()) {
            this.onPreviewClick();
        }
    }

    shouldHidePreview() {
        return !this.responsive.isXLarge() && !this.responsive.isLarge();
    }

    onPreviewClick() {
        this.isPreviewHide = !this.isPreviewHide;
    }

    onGoToPage() {
        this.notification.setVisible(true);
    }

    getNotificationVisible() {
        return this.notification.getVisible();
    }

    showDiscardOverlay() {
        this.discardChangesOverlay.setVisible(true);
        const sub = this.discardChangesOverlay.onOptionSelected.subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.removeSelectedPanel();
                this.emitClose();
            }
            sub.unsubscribe();
        });
    }
}
